/* istanbul ignore file */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import Button, { LoadingButton } from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import { ValueType } from '@atlaskit/select';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { createContactRelationship, patchContactRelationship } from '@atlassian/temp-proxies';
import type { BillingProfileOption } from '@css-xp/ppc-cart-page';
import { ChangeBillingProfile } from '@css-xp/ppc-cart-page';
import { routePaths, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { getRouteUrl, getCompanyNameRoute } from '@css-xp/ppc-utils';
import {
  NotificationTypes,
  useNotification,
  successNotificationBuilder,
  errorNotificationBuilder,
  useFeatureToggle,
} from '@css-xp/base';
import { failureMessage } from '@css-xp/ppc-common';
import { FREE_PRICING_TYPES } from '@css-xp/ppc-constants/src/subscription';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { useBulkTransferSubscriptions } from '../hooks/use-bulk-transfer-subscriptions';
import {
  CreateContactRelationshipsRequest,
  PatchContactRelationshipsRequest,
  useContactServicePayload,
  useFetchEccFromContactService,
} from '@css-xp/contact-resources';
import { ContactInfo } from '@css-xp/graphtypes/src/generated-types-new';
import isEmpty from 'lodash/isEmpty';

type TransferSubscriptionsInBulkProps = {
  isMoveSubscriptionsDisabled: boolean;
  onMoveSubscriptionsCompleted: () => void;
  dataState: {
    data: generatedTypesNew.ProductAppSubscription[];
    loading: boolean;
    refetch: (
      _variables?:
        | Partial<{
            searchOptions: generatedTypesNew.ConnectionSearchOptions;
            filterOptions: generatedTypesNew.FilterOption[];
          }>
        | undefined,
      _showApps?: boolean
    ) => void;
    error: string | undefined;
    totalCount: number;
  };
  areAllRowsSelected: boolean;
  selectedSubscriptionIds: string[];
  iconBefore?: React.ReactElement;
  shouldAllowBillingProfileTransferBasedOnPricingEligibility?: boolean;
  allowedPricingEligibilityForBillingProfileTransfer?: string[];
};

const StyledSelectBillingProfileContainer = styled.div({
  gap: '16px',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '3px',
  boxShadow: '0px 8px 12px 0px rgba(9, 30, 66, 0.15)',
  padding: '24px 24px 16px 24px',
});

const StyledSelectBillingProfileHeader = styled.label({
  fontSize: '20px',
  fontWeight: 400,
  lineHeight: '24px',
  color: '#172b4d',
  textAlign: 'left',
  fontVariationSettings: 'wdth 100',
});

const StyledBillingProfileActions = styled.div({
  display: 'flex',
  justifyContent: 'end',
  gap: '8px',
});

export const TransferSubscriptionsInBulk: React.FC<TransferSubscriptionsInBulkProps> = ({
  isMoveSubscriptionsDisabled,
  onMoveSubscriptionsCompleted,
  dataState,
  areAllRowsSelected,
  selectedSubscriptionIds,
  iconBefore,
  shouldAllowBillingProfileTransferBasedOnPricingEligibility = false,
  allowedPricingEligibilityForBillingProfileTransfer = [],
}) => {
  const { txaId } = useParams<{ txaId: string; billingProfileId: string }>();
  const { open: openModal, close: closeModal, isOpen } = useOpenClose();
  const { sendSuccessNotification, sendErrorNotification, notificationSent, setNotificationSent } = useNotification();

  const [alternateBillingProfiles, setAlternateBillingProfiles] = React.useState<generatedTypesNew.InvoiceGroup[]>([]);
  const [currentBillingProfile, setCurrentBillingProfile] = React.useState<generatedTypesNew.InvoiceGroup>();
  const [selectedBillingProfile, setSelectedBillingProfile] = useState<ValueType<BillingProfileOption> | null>(null);
  const [isAnyOfTheSelectedSubscriptionsMonthly, setIsAnyOfTheSelectedSubscriptionsMonthly] = React.useState<boolean>(
    false
  );
  const [areSubscriptionsBeingTransferred, setAreSubscriptionsBeingTransferred] = React.useState(false);
  const [isAnyOfTheSelectedSubscriptionsFree, setIsAnyOfTheSelectedSubscriptionsFree] = React.useState<boolean>(false);
  const [allPrimaryContacts, setAllPrimaryContacts] = useState<ContactInfo[]>([]);
  const [allSecondaryContacts, setAllSecondaryContacts] = useState<ContactInfo[]>([]);
  const [atlassianAccountIds, setAtlassianAccountIds] = useState<string[]>(['']);
  const [atlassianAccountEmails, setAtlassianAccountEmails] = useState<string[]>(['']);
  const [hasExistingPrimaryContact, setHasExistingPrimaryContact] = useState(false);
  const [hasAddedNewPrimaryContact, setHasAddedNewPrimaryContact] = React.useState(false);
  const [companyName, setCompanyName] = React.useState<string>('');
  const { getCreateContactRelationshipsPayload, getPatchPayloadForSetPrimaryContact } = useContactServicePayload();

  const [isEnabled] = useFeatureToggle();
  const isNonCommercialBillingProfilesDisabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_PREVENT_NON_COMMERCIAL_BILLING_PROFILES
  );
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isSandboxDecouplingEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING);
  const isFetchBillingProfilesViaCCPEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_BILLING_PROFILE_FROM_CCP);

  const isPaypalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);

  const showPrimaryContacts = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isNonCommercialChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NON_COMMERCIAL_PRICING);

  React.useEffect(() => {
    (async () => {
      const {
        alternateBillingProfilesForCustomer,
        currentBillingProfileForCustomer,
        companyName,
      } = await getTheAlternateBillingProfilesForTheCurrentCompany();
      setAlternateBillingProfiles(alternateBillingProfilesForCustomer);
      setCurrentBillingProfile(currentBillingProfileForCustomer);
      setCompanyName(companyName ?? '');
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      if (showPrimaryContacts && alternateBillingProfiles?.length > 0) {
        const invoiceGroupIds = alternateBillingProfiles?.map((invoiceGroupDetail: any) => invoiceGroupDetail?.id);
        const eccDetails = await getBulkECCDetails(invoiceGroupIds as string[], txaId);

        if (!isEmpty(eccDetails)) {
          const primaryContacts = eccDetails?.filter((contact: ContactInfo) => contact?.isPrimary);
          setAllPrimaryContacts(primaryContacts);

          const secondaryContacts = eccDetails?.filter((contact: ContactInfo) => !contact?.isPrimary);
          setAllSecondaryContacts(secondaryContacts);
        }
      }
    })();
  }, [alternateBillingProfiles]);

  React.useEffect(() => {
    if (showPrimaryContacts && selectedBillingProfile?.value) {
      if (allPrimaryContacts?.find((contact) => contact?.identifierId === selectedBillingProfile?.value)) {
        setHasExistingPrimaryContact(true);
      } else setHasExistingPrimaryContact(false);

      if (
        allSecondaryContacts
          ?.filter((contact: any) => contact?.identifierId === selectedBillingProfile?.value)
          ?.find((contact: any) => contact?.accountId === atlassianAccountIds[0]) &&
        !hasExistingPrimaryContact
      ) {
        setHasAddedNewPrimaryContact(false); // call patch
      } else if (!hasExistingPrimaryContact) setHasAddedNewPrimaryContact(true); // call create
    }
  }, [atlassianAccountIds, allSecondaryContacts, selectedBillingProfile?.value]);

  React.useEffect(() => {
    const allProducts = dataState.data?.map((eachData) => eachData.products).flat();
    setIsAnyOfTheSelectedSubscriptionsMonthly(
      areAllRowsSelected
        ? Boolean(
            allProducts?.some(
              (subscription: any) => subscription.billingFrequency === generatedTypesNew.BillingFrequency.MONTHLY
            )
          )
        : Boolean(
            allProducts
              ?.filter((subscription: any) => selectedSubscriptionIds.includes(subscription.id))
              ?.some(
                (subscription: any) => subscription.billingFrequency === generatedTypesNew.BillingFrequency.MONTHLY
              )
          )
    );

    setIsAnyOfTheSelectedSubscriptionsFree(
      areAllRowsSelected
        ? Boolean(
            allProducts?.some((subscription: any) => FREE_PRICING_TYPES.includes(subscription.offering?.pricingType))
          )
        : Boolean(
            allProducts
              ?.filter((subscription: any) => selectedSubscriptionIds.includes(subscription.id))
              ?.some((subscription: any) => FREE_PRICING_TYPES.includes(subscription.offering?.pricingType))
          )
    );
  }, [dataState?.data]);

  const {
    transferSubscriptions,
    getTheAlternateBillingProfilesForTheCurrentCompany,
    areBillingProfilesBeingFetched,
  } = useBulkTransferSubscriptions(
    isSandboxSupportForSubscriptionsEnabled,
    isFetchBillingProfilesViaCCPEnabled,
    isSandboxDecouplingEnabled
  );

  const { getBulkECCDetails, bulkEccDetailsLoading } = useFetchEccFromContactService();

  const showSuccessNotification = () => {
    if (!notificationSent) {
      const notificationOptions = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader('Success.')
        .withMessage(
          <p>
            {`Subscriptions successfully moved to billing profile ${selectedBillingProfile?.label}`}{' '}
            {
              <Link
                to={`${getRouteUrl(routePaths.billingProfileDetails, {
                  txaId,
                  billingProfileId: `${encodeURIComponent(selectedBillingProfile?.value ?? '')}`,
                })}`}
                onClick={(e) => e.stopPropagation()}
                className="subscriptions-transfer-billing-profile-name-link"
                data-testid="subscriptions-transfer-billing-profile-name-link"
              >
                Go to billing profile
              </Link>
            }
          </p>
        )
        .withDismissAfter(5000)
        .build();
      sendSuccessNotification(notificationOptions);
      setNotificationSent(true);
    }
  };

  const contactServiceHandler = async () => {
    setNotificationSent(false);

    try {
      let createOrPatchContactPayload;
      if (hasAddedNewPrimaryContact) {
        createOrPatchContactPayload = getCreateContactRelationshipsPayload({
          invoiceGroupId: selectedBillingProfile?.value as string,
          transactionAccountId: txaId,
          createContactIds: atlassianAccountIds,
          primaryContactId: atlassianAccountIds[0],
        });
      } else {
        createOrPatchContactPayload = getPatchPayloadForSetPrimaryContact({
          invoiceGroupId: selectedBillingProfile?.value as string,
          transactionAccountId: txaId,
          primaryContactId: atlassianAccountIds[0],
        });
      }
      if (!isEmpty(createOrPatchContactPayload)) {
        if (!hasAddedNewPrimaryContact) {
          await patchContactRelationship(createOrPatchContactPayload as PatchContactRelationshipsRequest);
        } else await createContactRelationship(createOrPatchContactPayload as CreateContactRelationshipsRequest);
      }
      return true;
    } catch (error: any) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Failure.')
          .withMessage(
            <p>
              {`Subscriptions can't be transferred because the primary contacts couldn't be updated.`}
              {<br />}
              {failureMessage()}
            </p>
          )
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
        setAreSubscriptionsBeingTransferred(false);
        closeModal();
      }
      return false;
    }
  };

  const onNextClicked = async () => {
    setAreSubscriptionsBeingTransferred(true);
    setNotificationSent(false);

    const hasPrimaryContactsSet =
      showPrimaryContacts && !hasExistingPrimaryContact ? await contactServiceHandler() : true;

    try {
      if (hasPrimaryContactsSet) {
        await transferSubscriptions({
          selectedSubscriptionIds,
          areAllRowsSelected,
          selectedBillingProfileId: selectedBillingProfile?.value,
        });

        showSuccessNotification();
        onMoveSubscriptionsCompleted();
      }
    } catch (e: any) {
      if (!notificationSent) {
        const notificationOptions = errorNotificationBuilder()
          .isVersion(NotificationTypes.FLAG)
          .withHeader('Failure.')
          .withMessage(
            <p>
              {`We are unable to move the subscriptions to the selected billing profile.`}
              {<br />}
              {`${e?.message}`}
              {failureMessage()}
            </p>
          )
          .withDismissAfter(5000)
          .build();
        sendErrorNotification(notificationOptions);
        setNotificationSent(true);
      }
    } finally {
      setAreSubscriptionsBeingTransferred(false);
      setNotificationSent(false);
      closeModal();
    }
  };

  const onMoveSubscriptionsClicked = async () => {
    openModal();
  };

  const isConfirmBtnDisabled = () => {
    const hasNotSelectedAnyBp = !selectedBillingProfile || selectedBillingProfile?.value === '';
    if (showPrimaryContacts) {
      return hasNotSelectedAnyBp || (!hasExistingPrimaryContact && atlassianAccountIds[0] === '');
    } else return hasNotSelectedAnyBp;
  };

  const customerBaseRoute = getCompanyNameRoute({
    txaId: txaId || '',
    companyName: companyName || '',
  });

  return (
    <div className="bulk-transfer-subscriptions-container">
      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={() => closeModal()}
            testId="select-billing-profile-for-bulk-subs-transfer"
            shouldCloseOnOverlayClick={false}
            shouldScrollInViewport={true}
          >
            <StyledSelectBillingProfileContainer>
              <StyledSelectBillingProfileHeader>Select billing profile</StyledSelectBillingProfileHeader>
              <SectionMessage>
                <p>All products within the same family will be automatically moved to this billing profile.</p>
              </SectionMessage>
              <ChangeBillingProfile
                billingProfilesInfo={alternateBillingProfiles}
                selectedBillingProfile={selectedBillingProfile}
                // @ts-ignore
                setSelectedBillingProfile={setSelectedBillingProfile}
                isDeferredPaymentMethodDisabled={
                  isAnyOfTheSelectedSubscriptionsMonthly || isAnyOfTheSelectedSubscriptionsFree
                }
                isNoPaymentMethodDisabled={true}
                areNonCommercialBillingProfilesDisabled={isNonCommercialBillingProfilesDisabled}
                shouldAllowBillingProfileTransferBasedOnPricingEligibility={
                  shouldAllowBillingProfileTransferBasedOnPricingEligibility
                }
                allowedPricingEligibilityForBillingProfileTransfer={allowedPricingEligibilityForBillingProfileTransfer}
                isPaypalEnabled={isPaypalEnabled}
                primaryContacts={allPrimaryContacts}
                secondaryCustomerContacts={allSecondaryContacts}
                showPrimaryContacts={showPrimaryContacts}
                atlassianAccountIds={atlassianAccountIds}
                setAtlassianAccountIds={setAtlassianAccountIds}
                atlassianAccountEmails={atlassianAccountEmails}
                setAtlassianAccountEmails={setAtlassianAccountEmails}
                disableByCurrency={isMultiCurrencyChangesEnabled}
                currentCurrency={currentBillingProfile?.currency}
                showPricingEligibility={isNonCommercialChangesEnabled}
                customerDetailsRoute={customerBaseRoute}
              />
              <StyledBillingProfileActions>
                <Button onClick={closeModal}>Cancel</Button>
                <LoadingButton
                  appearance="primary"
                  onClick={() => onNextClicked()}
                  isDisabled={isConfirmBtnDisabled()}
                  isLoading={areSubscriptionsBeingTransferred}
                >
                  Confirm
                </LoadingButton>
              </StyledBillingProfileActions>
            </StyledSelectBillingProfileContainer>
          </Modal>
        )}
      </ModalTransition>
      <div className="action-buttons action-move-subscriptions" data-testId="action-move-subscriptions">
        <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.UPDATE} resource={ADVOCATE_RESOURCE_TYPE.SUBSCRIPTION}>
          <LoadingButton
            isLoading={areBillingProfilesBeingFetched || bulkEccDetailsLoading}
            onClick={onMoveSubscriptionsClicked}
            className="action-buttons"
            testId="action-move-subscriptions"
            isDisabled={isMoveSubscriptionsDisabled}
            iconBefore={iconBefore}
          >
            Move subscriptions
          </LoadingButton>
        </AdvocateAccessWall>
      </div>
    </div>
  );
};
